import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { kyc } from '@norvix/types';
import { useEffect, useState } from 'react';
import { apiFetch } from '../../api/client.js';
import { settingsHistoryKey, type SettingsHistoryEntry } from '../../api/settings-history.api.js';
import { MONITORING_RESTORE, fourEyesRestoreApi } from '../../api/settings-restore.api.js';
import { APP_LOCALE } from '../../utils/locale.js';

import { MONITORING_REASON_MIN, monitoringPendingChanges } from './monitoring-changes.js';
import { SettingsHistoryButton, useSettingsLastChanged } from './SettingsHistory.js';
import { FourEyesRestore } from './SettingsRestore.js';
import { SettingsSaveBar } from './SettingsSaveBar.js';
import { SettingsSectionHeader } from './SettingsSectionHeader.js';
import { useSectionRestore } from './useSettingsAccess.js';

const PATH = '/admin/settings/transaction-monitoring';
const MONITORING_RESTORE_API = fourEyesRestoreApi(MONITORING_RESTORE);
const FIELD_LABELS: Record<string, string> = {
  AMOUNT_EUR: 'Amount in EUR',
  CUSTOMER_RISK: 'Customer risk rating',
  COUNTERPARTY_COUNTRY: 'Counterparty country',
};
function decimal(minor: string): string {
  const value = minor.padStart(3, '0');
  return `${value.slice(0, -2)}.${value.slice(-2)}`;
}
export function monitoringMinor(value: string): string {
  if (!/^\d{1,17}(\.\d{1,2})?$/.test(value.trim()))
    throw new Error('Enter an exact EUR amount with up to two decimal places.');
  const [whole = '0', fraction = ''] = value.trim().split('.');
  const minor = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
  if (minor > 9_223_372_036_854_775_807n) throw new Error('The EUR amount is too large.');
  return minor.toString();
}
const describe = (rule: kyc.MonitoringRule): string =>
  rule.field === 'AMOUNT_EUR'
    ? `Exceeds €${decimal(rule.amountEurMinor)}`
    : `Is one of: ${rule.values.join(', ')}`;

export function TransactionMonitoringPage(): JSX.Element {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ['monitoring-rules'],
    queryFn: () => apiFetch<kyc.MonitoringRulesState>(PATH),
  });
  const [rules, setRules] = useState<kyc.MonitoringRule[]>([]);
  const [revision, setRevision] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [editor, setEditor] = useState<kyc.MonitoringRule | null>(null);
  const [amount, setAmount] = useState('');
  const [values, setValues] = useState('');
  const [reason, setReason] = useState('');
  const [dialog, setDialog] = useState<'activate' | 'test' | 'history' | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savedAt, setSavedAt] = useState(0);
  const lastChanged = useSettingsLastChanged('MONITORING_RULES');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [test, setTest] = useState({
    direction: 'OUT',
    amount: '',
    customerRisk: '',
    counterpartyCountry: '',
  });
  const [evaluation, setEvaluation] = useState<kyc.LocalMonitoringEvaluation | null>(null);
  const restore = useSectionRestore({ group: 'compliance', id: 'transaction-monitoring' });
  const [restoreTarget, setRestoreTarget] = useState<string | null>(null);
  useEffect(() => {
    if (query.data && !dirty) {
      setRules(query.data.draft?.rules ?? query.data.active?.rules ?? []);
      setRevision(query.data.revision);
    }
  }, [query.data, dirty]);
  const change = (next: kyc.MonitoringRule[]): void => {
    setRules(next);
    setDirty(true);
    setSuccess('');
    setSaveError('');
  };
  const saved = query.data?.draft?.rules ?? query.data?.active?.rules ?? [];
  // What the save bar shows (DEV-137); switching a rule off needs a reason.
  const changes = dirty ? monitoringPendingChanges(saved, rules) : [];
  const unsaved = changes.length > 0;
  const refreshHistory = (): void => {
    void client.invalidateQueries({ queryKey: settingsHistoryKey('MONITORING_RULES') });
  };
  const saveDraft = async (given?: string): Promise<void> => {
    setSaving(true);
    setSaveError('');
    try {
      const state = await apiFetch<kyc.MonitoringRulesState>(`${PATH}/draft`, {
        method: 'PUT',
        body: { expectedRevision: revision, rules, ...(given === undefined ? {} : { reason: given }) },
      });
      client.setQueryData(['monitoring-rules'], state);
      setDirty(false);
      setSavedAt(Date.now());
      setSuccess('Draft saved. Active rules have not changed.');
      refreshHistory();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'The draft could not be saved.');
    } finally {
      setSaving(false);
    }
  };
  const discard = (): void => {
    setRules(saved);
    setDirty(false);
    setSaveError('');
  };
  const openRule = (rule?: kyc.MonitoringRule): void => {
    const next = rule ?? {
      id: crypto.randomUUID(),
      name: '',
      enabled: true,
      direction: 'ALL',
      field: 'AMOUNT_EUR',
      amountEurMinor: '',
      values: [],
    };
    setEditor({ ...next });
    setAmount(next.amountEurMinor ? decimal(next.amountEurMinor) : '');
    setValues(next.values.join(', '));
    setError('');
  };
  const applyRule = (): void => {
    if (!editor) return;
    try {
      const next = {
        ...editor,
        name: editor.name.trim(),
        amountEurMinor: editor.field === 'AMOUNT_EUR' ? monitoringMinor(amount) : '',
        values:
          editor.field === 'AMOUNT_EUR'
            ? []
            : [
                ...new Set(
                  values
                    .split(',')
                    .map((value) => value.trim().toUpperCase())
                    .filter(Boolean),
                ),
              ],
      };
      if (!next.name || next.name.length > 120)
        throw new Error('Give this rule a name of 1–120 characters.');
      if (next.field !== 'AMOUNT_EUR' && !next.values.length)
        throw new Error('Enter at least one value.');
      if (
        next.field === 'CUSTOMER_RISK' &&
        next.values.some((value) => !['LOW', 'MEDIUM', 'HIGH'].includes(value))
      )
        throw new Error('Use LOW, MEDIUM or HIGH.');
      change(
        rules.some((rule) => rule.id === next.id)
          ? rules.map((rule) => (rule.id === next.id ? next : rule))
          : [...rules, next],
      );
      setEditor(null);
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Check the rule.');
    }
  };
  const submit = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      if (dialog === 'test') {
        setEvaluation(
          await apiFetch<kyc.LocalMonitoringEvaluation>(`${PATH}/test`, {
            method: 'POST',
            body: {
              draftId: query.data?.draft?.id,
              direction: test.direction,
              eurAmountMinor: test.amount ? monitoringMinor(test.amount) : '',
              customerRisk: test.customerRisk,
              counterpartyCountry: test.counterpartyCountry.toUpperCase(),
            },
          }),
        );
      } else {
        const state = await apiFetch<kyc.MonitoringRulesState>(`${PATH}/activate`, {
          method: 'POST',
          body: { expectedRevision: revision, draftId: query.data?.draft?.id, reason: reason.trim() },
        });
        client.setQueryData(['monitoring-rules'], state);
        setDirty(false);
        setDialog(null);
        setReason('');
        setSuccess('Rules activated. New screening decisions use this version.');
        refreshHistory();
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'The operation could not be completed.');
    } finally {
      setBusy(false);
    }
  };
  if (query.isPending) return <CircularProgress aria-label="Loading monitoring rules" />;
  if (query.isError)
    return (
      <Alert severity="error" action={<Button onClick={() => void query.refetch()}>Retry</Button>}>
        Monitoring rules could not be loaded.
      </Alert>
    );
  const active = query.data?.active;
  const draft = query.data?.draft;
  return (
    <Stack spacing={2.5}>
      <SettingsSectionHeader
        title="Transaction monitoring"
        lastChanged={lastChanged}
        actions={
          <>
            <Button size="small" onClick={() => setDialog('history')}>Versions</Button>
            <SettingsHistoryButton subject="MONITORING_RULES" title="Transaction monitoring" restoreLabel={restore.fourEyes ? 'Propose restore' : 'Restore this version'}
              {...(restore.note === undefined ? {} : { restoreNote: restore.note })}
              {...(restore.canRestore ? { onRestore: (entry: SettingsHistoryEntry) => setRestoreTarget(entry.restoreTarget ?? null) } : {})} />
          </>
        }
      >
        Manage Norvix rules and the compliance review they require. Every change is recorded; switching a rule
        off needs a reason.
      </SettingsSectionHeader>
      <Alert severity="info">
        Rules are maintained manually in Norvix. Platforma365 screening runs separately; these rules
        add to its checks. Matching or missing required inputs need recorded compliance clearance.
      </Alert>
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {/* DEV-142: a restore changes who gets flagged, so a second approver agrees first. */}
      <FourEyesRestore
        api={MONITORING_RESTORE_API}
        base={MONITORING_RESTORE}
        subject="MONITORING_RULES"
        target={restoreTarget}
        onClearTarget={() => setRestoreTarget(null)}
        canDecide={restore.canRestore}
        editing={unsaved}
        onApproved={() => { setDirty(false); void query.refetch(); }}
      />
      {error && !editor && !dialog && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 310px' },
          alignItems: 'start',
          gap: 2.5,
        }}
      >
        <Paper variant="outlined" sx={{ p: 2.5, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ flex: 1 }}>
              Custom rules
            </Typography>
            <Chip
              size="small"
              label={unsaved ? 'Unsaved changes' : draft ? 'Saved draft' : 'Active rules'}
              color={unsaved || draft ? 'warning' : 'default'}
            />
            <Button onClick={() => openRule()} disabled={rules.length >= 100}>
              Add rule
            </Button>
          </Stack>
          {!rules.length ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography>No custom rules configured</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                Add thresholds and country lists approved by your compliance team.
              </Typography>
              <Button variant="outlined" onClick={() => openRule()}>
                Create a rule
              </Button>
            </Box>
          ) : (
            <Stack divider={<Divider />} spacing={1.5}>
              {rules.map((rule) => (
                <Stack key={rule.id} direction="row" alignItems="center" spacing={1}>
                  <Switch
                    checked={rule.enabled}
                    inputProps={{ 'aria-label': `Enable ${rule.name}` }}
                    onChange={(_, enabled) =>
                      change(
                        rules.map((item) => (item.id === rule.id ? { ...item, enabled } : item)),
                      )
                    }
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={600}>{rule.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {FIELD_LABELS[rule.field]} · {describe(rule)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {rule.direction === 'ALL'
                        ? 'All directions'
                        : rule.direction === 'IN'
                          ? 'Incoming'
                          : 'Outgoing'}{' '}
                      · Compliance clearance required
                    </Typography>
                  </Box>
                  <Button size="small" onClick={() => openRule(rule)}>
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => change(rules.filter((item) => item.id !== rule.id))}
                  >
                    Remove
                  </Button>
                </Stack>
              ))}
            </Stack>
          )}
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Any enabled rule can require review. Save changes as a draft, test them and activate
            explicitly.
          </Typography>
        </Paper>
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Active configuration
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {active
                ? `${active.rules.filter((rule) => rule.enabled).length} enabled rules`
                : 'No active custom rules'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {active
                ? `Activated ${new Date(active.activatedAt).toLocaleString(APP_LOCALE)} by ${active.activatedBy}`
                : 'Provider screening and the existing clearance controls remain in force.'}
            </Typography>
            {draft && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2">Saved draft · {draft.rules.length} rules</Typography>
                <Stack spacing={1} sx={{ mt: 1.5 }}>
                  <Button
                    variant="outlined"
                    disabled={unsaved}
                    onClick={() => {
                      setDialog('test');
                      setEvaluation(null);
                      setError('');
                    }}
                  >
                    Test saved draft
                  </Button>
                  <Button
                    variant="contained"
                    disabled={unsaved}
                    onClick={() => {
                      setDialog('activate');
                      setReason('');
                      setError('');
                    }}
                  >
                    Activate draft
                  </Button>
                </Stack>
                {unsaved && (
                  <Typography variant="caption">
                    Save your changes before testing or activating.
                  </Typography>
                )}
              </>
            )}
          </Paper>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              EUR monitoring value
            </Typography>
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              <Typography variant="body2">ECB daily reference rates</Typography>
              <Typography variant="body2" color="text.secondary">
                Latest published rate at first screening, up to 7 calendar days old. Round once to
                the nearest cent, half-even.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Missing or older rates require recorded compliance clearance.
              </Typography>
            </Stack>
          </Paper>
        </Stack>
      </Box>
      <SettingsSaveBar
        sticky
        changes={changes}
        saving={saving}
        onSave={(given) => void saveDraft(given)}
        onDiscard={discard}
        error={saveError}
        savedAt={savedAt}
        reasonMinLength={MONITORING_REASON_MIN}
        reasonPrompt="Switching a rule off stops it screening payments once this draft is activated, so the reason is kept in the History."
      />
      <Dialog open={editor !== null} onClose={() => setEditor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {rules.some((rule) => rule.id === editor?.id) ? 'Edit rule' : 'Add rule'}
        </DialogTitle>
        <DialogContent>
          {editor && (
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                autoFocus
                label="Rule name"
                value={editor.name}
                onChange={(event) => setEditor({ ...editor, name: event.target.value })}
                inputProps={{ maxLength: 120 }}
              />
              <TextField
                select
                label="Direction"
                value={editor.direction}
                onChange={(event) => setEditor({ ...editor, direction: event.target.value })}
              >
                {[
                  ['ALL', 'All directions'],
                  ['IN', 'Incoming'],
                  ['OUT', 'Outgoing'],
                ].map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Condition"
                value={editor.field}
                onChange={(event) => {
                  setEditor({ ...editor, field: event.target.value });
                  setValues('');
                }}
              >
                {Object.entries(FIELD_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              {editor.field === 'AMOUNT_EUR' ? (
                <TextField
                  label="Amount exceeds (EUR)"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                  helperText="The exact threshold is not a match; only amounts above it match."
                />
              ) : (
                <TextField
                  label={editor.field === 'CUSTOMER_RISK' ? 'Risk ratings' : 'Country codes'}
                  value={values}
                  onChange={(event) => setValues(event.target.value)}
                  helperText={
                    editor.field === 'CUSTOMER_RISK'
                      ? 'Comma-separated: LOW, MEDIUM, HIGH'
                      : 'Comma-separated ISO country codes, for example DE, FR. Use your approved country list.'
                  }
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={editor.enabled}
                    onChange={(_, enabled) => setEditor({ ...editor, enabled })}
                  />
                }
                label="Enabled when this ruleset is activated"
              />
              <Alert severity="info">
                Action: require recorded compliance clearance. Approval remains a separate decision.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditor(null)}>Cancel</Button>
          <Button variant="contained" onClick={applyRule}>
            Apply to draft
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={dialog !== null}
        onClose={busy ? undefined : () => setDialog(null)}
        maxWidth={dialog === 'history' ? 'md' : 'sm'}
        fullWidth
      >
        <DialogTitle>
          {dialog === 'activate'
              ? 'Activate rules draft'
              : dialog === 'test'
                ? 'Test saved draft'
                : 'Version history'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {dialog === 'activate' && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning">
                {`Activate ${draft?.rules.filter((rule) => rule.enabled).length ?? 0} enabled rules. Changed rules invalidate earlier screening clearance on the next decision. Previously completed payments are not reprocessed.`}
              </Alert>
              <TextField
                autoFocus
                label="Reason for this change"
                multiline
                minRows={3}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                helperText="10–2000 characters. Recorded with your identity and timestamp."
                inputProps={{ maxLength: 2000 }}
              />
            </Stack>
          )}
          {dialog === 'test' && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2">
                Synthetic input only. This does not contact Platforma365 or clear a real payment.
              </Typography>
              <TextField
                select
                label="Direction"
                value={test.direction}
                onChange={(event) => setTest({ ...test, direction: event.target.value })}
              >
                <MenuItem value="IN">Incoming</MenuItem>
                <MenuItem value="OUT">Outgoing</MenuItem>
              </TextField>
              <TextField
                label="EUR amount"
                value={test.amount}
                onChange={(event) => setTest({ ...test, amount: event.target.value })}
                helperText="Leave blank to test an unavailable valuation."
              />
              <TextField
                select
                label="Customer risk"
                value={test.customerRisk}
                onChange={(event) => setTest({ ...test, customerRisk: event.target.value })}
              >
                {['', 'LOW', 'MEDIUM', 'HIGH'].map((value) => (
                  <MenuItem key={value} value={value}>
                    {value || 'Unavailable'}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Counterparty country code"
                value={test.counterpartyCountry}
                onChange={(event) => setTest({ ...test, counterpartyCountry: event.target.value })}
                inputProps={{ maxLength: 3 }}
              />
              {evaluation && (
                <>
                  <Alert severity={evaluation.reviewRequired ? 'warning' : 'success'}>
                    {evaluation.reviewRequired
                      ? 'Recorded compliance clearance required'
                      : 'No local rule requires clearance. Provider checks still apply.'}
                  </Alert>
                  {evaluation.findings.map((finding) => (
                    <Typography key={finding.ruleId} variant="body2">
                      {finding.ruleName}: {finding.outcome} — {finding.reason}
                    </Typography>
                  ))}
                </>
              )}
            </Stack>
          )}
          {dialog === 'history' && (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Version</TableCell>
                    <TableCell>Saved by / date</TableCell>
                    <TableCell>Activation</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {query.data?.history.map((version) => (
                    <TableRow key={version.id}>
                      <TableCell sx={{ overflowWrap: 'anywhere' }}>
                        {version.id}
                        <Typography variant="caption" display="block">
                          {version.rules.length} rules
                          {active?.id === version.id
                            ? ' · Active'
                            : draft?.id === version.id
                              ? ' · Current draft'
                              : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {version.createdBy}
                        <Typography variant="caption" display="block">
                          {new Date(version.createdAt).toLocaleString(APP_LOCALE)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {version.activatedAt
                          ? `${version.activatedBy} · ${new Date(version.activatedAt).toLocaleString(APP_LOCALE)}`
                          : 'Not activated'}
                      </TableCell>
                      <TableCell>
                        {version.reason || 'No reason given'}
                        {version.activationReason && (
                          <Typography variant="body2">
                            Activation: {version.activationReason}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="caption">
                Latest 50 saved versions. Activated versions are retained.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setDialog(null)}>
            Close
          </Button>
          {dialog !== 'history' && (
            <Button
              variant="contained"
              disabled={busy || (dialog !== 'test' && reason.trim().length < MONITORING_REASON_MIN)}
              onClick={() => void submit()}
            >
              {busy
                ? 'Working…'
                : dialog === 'activate'
                  ? 'Activate'
                  : 'Run test'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
